import {
  Component,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  EventEmitter,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { EmployeeService } from '../../services/employee.service';
import { Shift } from '../../models/employee.models';
import { SearchbarComponent } from '../../../shared/components/searchbar/searchbar.component';
import { AddButtonComponent } from '../../../shared/components/add-button/add-button.component';
import { EmptyResultComponent } from '../../../shared/components/empty-result/empty-result.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import {
  SuccessModalComponent,
  SuccessModalData,
} from '../../../shared/components/success-modal/success-modal.component';
import {
  ConfirmationModalComponent,
  ConfirmationModalData,
} from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import {
  AddShiftModalComponent,
  AddShiftFormData,
} from '../add-shift-modal/add-shift-modal.component';
import { TableUtils, DragScrollConfig } from '../../utils/table.utils';

/**
 * Displays and manages the employee shifts tab, including listing, searching, adding, editing, and deleting shifts.
 * @component
 * @selector app-shifts-tab
 * @example
 *   <app-shifts-tab (shiftChanged)="handler()"></app-shifts-tab>
 * @output EventEmitter<void> shiftChanged - Fired after a shift is saved or deleted so the parent can reload related shift data.
 *
 * Key methods:
 * - load(): Reloads shifts and resets the current search term when the tab becomes active.
 * - onShiftSearchChange(searchTerm: string): Updates the search term and debounces server-side search.
 * - onAddShift(): Opens the add shift modal in create mode.
 * - onEditShift(shift: Shift): Opens the add shift modal in edit mode for the selected shift.
 * - onDeleteShift(shift: Shift): Opens the delete confirmation modal for the selected shift.
 * - onSaveShift(formData: AddShiftFormData): Closes the modal, shows success feedback, refreshes the list, and emits shiftChanged.
 * - onConfirmationConfirm(): Confirms deletion of the selected shift.
 * - formatWorkingDaysTimings(workingDays: string): Formats stored working-day/timing strings for display.
 * - formatTime(timeString: string): Converts 24-hour time strings to 12-hour AM/PM format.
 *
 * Lifecycle hooks:
 * - ngOnInit(): Component initialization hook.
 * - ngAfterViewInit(): Reinitializes drag scrolling after the view is rendered.
 * - ngOnDestroy(): Cleans up subscriptions, timers, and table utilities to prevent memory leaks.
 */
@Component({
  selector: 'app-shifts-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SearchbarComponent,
    AddButtonComponent,
    EmptyResultComponent,
    LoadingTableShimmerComponent,
    SuccessModalComponent,
    ConfirmationModalComponent,
    AddShiftModalComponent,
  ],
  providers: [NotificationService],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './shifts-tab.component.html',
})
export class ShiftsTabComponent implements OnDestroy, AfterViewInit {
  @ViewChild('shiftsTableScrollWrapper') shiftsTableScrollWrapper!: ElementRef;

  /** Fired after a shift is saved or deleted so the parent can tell shift-schedule tab to reload. */
  @Output() shiftChanged = new EventEmitter<void>();

  private destroy$ = new Subject<void>();
  private shiftsDragScrollConfig!: DragScrollConfig;
  private shiftSearchTimeout: ReturnType<typeof setTimeout> | null = null;

  shifts: Shift[] = [];
  filteredShifts: Shift[] = [];
  searchTerm = '';
  loadingShifts = false;

  activeShiftActionsDropdown: string | null = null;
  selectedShiftForDropdown: Shift | null = null;

  showAddShiftModal = false;
  editShiftMode = false;
  editShiftData: AddShiftFormData | null = null;
  editShiftId: string | null = null;

  showSuccessModal = false;
  successModalData: SuccessModalData = {
    title: 'Shift Added!',
    description:
      'A new shift has been created successfully! You can edit the shift timings anytime.',
    buttonText: 'Okay',
    showTimeInfo: false,
  };

  showConfirmationModal = false;
  confirmationModalData: ConfirmationModalData = {
    title: 'Delete Shift?',
    description: 'Are you sure you want to delete this shift? This action cannot be undone.',
    cancelText: 'Cancel',
    confirmText: 'Delete Shift',
    imageSrc: 'assets/images/empty_screen.png',
    imageAlt: 'Delete confirmation illustration',
    showTimeInfo: false,
    timeInfo: '',
  };
  shiftToDelete: Shift | null = null;

  shimmerColumns: ShimmerColumn[] = [
    { label: 'Shift Name', width: 'md' },
    { label: 'Working Days / Timings', width: 'lg' },
    { label: 'Off Days', width: 'sm' },
    { label: 'Actions', width: 'xs' },
  ];

  constructor(
    private employeeService: EmployeeService,
    private messageService: NotificationService
  ) {}

  ngAfterViewInit(): void {
    this.reinitDragScroll();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.shiftSearchTimeout) clearTimeout(this.shiftSearchTimeout);
    TableUtils.cleanup();
  }

  /** Called by parent when this tab becomes active. */
  load(): void {
    this.searchTerm = '';
    this.loadShifts();
  }

  /**
   * Loads shifts from the employee service and updates the component state.
   * @example
   * loadShifts()
   * void
   * @returns {void} No return value.
   **/
  private loadShifts(): void {
    this.loadingShifts = true;
    this.employeeService
      .loadShifts()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: shifts => {
          this.shifts = shifts || [];
          this.filteredShifts = [...this.shifts];
          this.loadingShifts = false;
          this.reinitDragScroll();
        },
        error: () => {
          this.shifts = [];
          this.filteredShifts = [];
          this.loadingShifts = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'A technical error occurred. Please try again.',
          });
        },
      });
  }

  /**
   * Loads shifts matching the current search term and updates the displayed shift lists.
   * @example
   * loadShiftsWithSearch()
   * void
   * @returns {void} No return value.
   **/
  private loadShiftsWithSearch(): void {
    this.employeeService
      .loadShiftsWithSearch(this.searchTerm)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: shifts => {
          this.shifts = shifts || [];
          this.filteredShifts = [...this.shifts];
          this.reinitDragScroll();
        },
        error: () => {
          this.shifts = [];
          this.filteredShifts = [];
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'A technical error occurred while searching shifts.',
          });
        },
      });
  }

  onShiftSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    if (this.shiftSearchTimeout) clearTimeout(this.shiftSearchTimeout);
    this.shiftSearchTimeout = setTimeout(() => this.loadShiftsWithSearch(), 300);
  }

  onAddShift(): void {
    this.editShiftMode = false;
    this.editShiftData = null;
    this.editShiftId = null;
    this.showAddShiftModal = true;
  }

  onCloseAddShiftModal(): void {
    this.showAddShiftModal = false;
    this.editShiftMode = false;
    this.editShiftData = null;
    this.editShiftId = null;
  }

  onSaveShift(_formData: AddShiftFormData): void {
    this.successModalData = this.editShiftMode
      ? {
          title: 'Shift Updated Successfully!',
          description: 'Your shift has been updated.',
          buttonText: 'Okay',
          showTimeInfo: false,
        }
      : {
          title: 'Shift Added!',
          description: 'A new shift has been created successfully!',
          buttonText: 'Okay',
          showTimeInfo: false,
        };
    this.showAddShiftModal = false;
    this.showSuccessModal = true;
    this.loadShifts();
    this.shiftChanged.emit();
  }

  onSuccessModalClose(): void {
    this.showSuccessModal = false;
  }

  onDeleteShift(shift: Shift): void {
    this.activeShiftActionsDropdown = null;
    this.selectedShiftForDropdown = null;
    this.shiftToDelete = shift;
    this.showConfirmationModal = true;
  }

  onConfirmationCancel(): void {
    this.showConfirmationModal = false;
    this.shiftToDelete = null;
  }

  onConfirmationConfirm(): void {
    if (this.shiftToDelete) this.deleteShift(this.shiftToDelete.id);
  }

  /**
   * Deletes a shift and updates the UI based on the result.
   * @example
   * deleteShift('shift123')
   * void
   * @param {string} shiftId - The ID of the shift to delete.
   * @returns {void} No return value.
   **/
  private deleteShift(shiftId: string): void {
    this.employeeService
      .deleteShift(shiftId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.status === true) {
            this.showConfirmationModal = false;
            this.shiftToDelete = null;
            this.loadShifts();
            this.shiftChanged.emit();
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Shift deleted successfully!',
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: response.message || 'Failed to delete shift',
            });
          }
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete shift. Please try again.',
          });
        },
      });
  }

  onEditShift(shift: Shift): void {
    this.activeShiftActionsDropdown = null;
    this.selectedShiftForDropdown = null;
    this.editShiftId = shift.id;
    this.editShiftMode = true;
    this.showAddShiftModal = true;
  }

  toggleShiftActionsDropdown(event: Event, shiftId: string): void {
    if (this.activeShiftActionsDropdown === shiftId) {
      this.activeShiftActionsDropdown = null;
      this.selectedShiftForDropdown = null;
    } else {
      this.activeShiftActionsDropdown = shiftId;
      this.selectedShiftForDropdown = this.shifts.find(s => s.id === shiftId) || null;
      this.positionDropdown(event, '.actions-dropdown-menu[id*="shift-dropdown"]');
    }
  }

  getAddShiftAction(): () => void {
    return () => this.onAddShift();
  }

  formatWorkingDaysTimings(workingDays: string): string {
    if (!workingDays) return '';
    const parts = workingDays.split(' ');
    if (parts.length >= 6) {
      return `${parts[0]} - ${parts[2]} ${this.formatTime(parts[3])} - ${this.formatTime(parts[5])}`;
    } else if (parts.length >= 4) {
      return `${parts[0]} - ${parts[2]} ${this.formatTime(parts[3])} - ${this.formatTime(parts[5] || parts[4])}`;
    }
    return workingDays;
  }

  /**
   * Formats a time string from 24-hour HH:MM format to 12-hour AM/PM format.
   * @example
   * formatTime('13:30')
   * "1:30 PM"
   * @param {string} timeString - Time string in HH:MM format to format.
   * @returns {string} Formatted time string in 12-hour AM/PM format, or the original value if it cannot be parsed.
   */
  formatTime(timeString: string): string {
    if (!timeString) return '';
    const time = timeString.split(':');
    if (time.length >= 2) {
      let hours = parseInt(time[0]);
      const minutes = time[1];
      const period = hours >= 12 ? 'PM' : 'AM';
      if (hours === 0) hours = 12;
      else if (hours > 12) hours -= 12;
      return `${hours}:${minutes} ${period}`;
    }
    return timeString;
  }

  trackByShiftId(_: number, shift: Shift): string {
    return shift.id;
  }

  /**
   * Positions a dropdown menu relative to an actions button, adjusting placement to fit within the viewport.
   * @example
   * positionDropdown(event, '.dropdown-menu')
   * void
   * @param {Event} event - The triggering event used to locate the related actions button.
   * @param {string} selector - CSS selector used to find the dropdown element to position.
   * @returns {void} This function does not return a value.
   **/
  private positionDropdown(event: Event, selector: string): void {
    requestAnimationFrame(() => {
      const button = (event.target as HTMLElement).closest('.actions-button') as HTMLElement;
      const dropdown = document.querySelector(selector) as HTMLElement;
      if (button && dropdown) {
        const rect = button.getBoundingClientRect();
        const dropdownHeight = dropdown.offsetHeight || 88;
        const viewportHeight = window.innerHeight;
        dropdown.style.position = 'fixed';
        dropdown.style.zIndex = '9999';
        if (viewportHeight - rect.bottom >= dropdownHeight + 10) {
          dropdown.style.top = `${rect.bottom + 5}px`;
          dropdown.style.bottom = 'auto';
        } else {
          dropdown.style.top = 'auto';
          dropdown.style.bottom = `${viewportHeight - rect.top + 5}px`;
        }
        dropdown.style.left = `${rect.right - dropdown.offsetWidth}px`;
      }
    });
  }

  private reinitDragScroll(): void {
    setTimeout(() => {
      if (this.shiftsTableScrollWrapper) {
        this.shiftsDragScrollConfig = TableUtils.createDragScrollConfig(
          this.shiftsTableScrollWrapper
        );
        TableUtils.initializeDragScrolling(this.shiftsDragScrollConfig);
      }
    }, 100);
  }
}
